import React, { useState, useEffect } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useForm, FormProvider, useWatch } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useGlobalSlice } from "app/slice";
import { PhoneSelector } from "./phone-selector";
import { VLEMobileSimDialog } from "./vle-mobile-sim-dialog";
import { SimCardNumber } from "./sim-card-number";
import { toast } from "app/components/ui/use-toast";

import { Button } from "app/components/ui/button";

type FormValue = {
	brand: string;
	model: string;
	simType: string;
	simNumber: string;
	imeiNumber: string;
};

interface VerificationResult {
  success: boolean;
  message: string;
  deviceId?: string;
  imei?: string;
}

interface OutletContext {
	isSimCardView: boolean;
	setIsSimCardView: React.Dispatch<React.SetStateAction<boolean>>;
	compatResult: any;
	setCompatResult: React.Dispatch<React.SetStateAction<any>>;
}

export const DeviceCompatibilty = () => {
	const [showVLESimDialog, setShowVLESimDialog] = useState(false);
	const [simVerified, setSimVerified] = useState(false);
	const [imeiVerified, setImeiVerified] = useState(false);
	const { isSimCardView, setIsSimCardView, compatResult, setCompatResult } = useOutletContext<OutletContext>();
	const form = useForm<FormValue>({
		defaultValues: {
			brand: "",
			model: "",
			simType: "",
			simNumber: "",
			imeiNumber: "",
		},
		mode: "onChange",
	});
	const dispatch = useDispatch();
	const { actions } = useGlobalSlice();

	const navigate = useNavigate();
	const { control, setValue, handleSubmit, watch } = form;

	// Watch form data for verification status
	const simNumber = watch("simNumber");
	const imeiNumber = watch("imeiNumber");

	// Check if both SIM and IMEI are verified successfully
	const areSimAndImeiVerified = simVerified && imeiVerified;

	const onSubmit = async (data: FormValue) => {
    if (isSimCardView) {
      // Check if both SIM and IMEI are verified
      if (!data.simNumber || !data.imeiNumber) {
        toast({
          title: "Incomplete Verification",
          description: "Please verify both SIM and IMEI numbers before continuing",
          variant: "error",
        });
        return;
      }

      // Update compatibility data on successful verification
      const updatedCompatibilityData = {
        ...compatResult,
        deviceId: data.imeiNumber,
        enrollment_type: "HANDOVER",
        is_esim: data.simType === "eSim" ? "Y" : "N"
      };
      
      setCompatResult(updatedCompatibilityData);
      dispatch(actions.setCompatibilityData({ CD: updatedCompatibilityData }));
      // Navigate directly to number-type page after SIM verification
      navigate("/single-network-number-type");
    } else {
      const isPhysicalSim = data.simType === "sim";

      if (isPhysicalSim) {
        setShowVLESimDialog(true);
      } else {
        // For eSIM, update compatibility data immediately
        const updatedCompatibilityData = {
          ...compatResult,
          is_esim: "Y"
        };
        
        setCompatResult(updatedCompatibilityData);
        dispatch(actions.setCompatibilityData({ CD: updatedCompatibilityData }));
        navigate("/single-network-number-type");
      }
    }
  };

	const brand = useWatch({ control, name: "brand" });
	const model = useWatch({ control, name: "model" });
	const simType = useWatch({ control, name: "simType" });

	// Handle verification status changes from SimCardNumber component
	const handleVerificationChange = (simVerified: boolean, imeiVerified: boolean) => {
		setSimVerified(simVerified);
		setImeiVerified(imeiVerified);
	};

	return (
		<React.Fragment>
			<FormProvider {...form}>
				<form
					className="w-full max-w-[31rem] md:mx-auto space-y-4 sm:space-y-5 lg:space-y-6"
					onSubmit={handleSubmit(onSubmit)}
				>
					{isSimCardView ? (
						<>
							<SimCardNumber
								onVerificationChange={handleVerificationChange}
							/>
							{areSimAndImeiVerified && (
								<Button type="submit" variant="primary" className="w-full">
									Continue
								</Button>
							)}
						</>
					) : (
						<>
							<PhoneSelector imeiNumber={imeiNumber} control={control} setValue={setValue} />
							{brand && model && simType && (
								<Button type="submit" variant="primary">
									Continue
								</Button>
							)}
						</>
					)}
				</form>
			</FormProvider>
			<VLEMobileSimDialog
				showDialog={showVLESimDialog}
				setShowDialog={setShowVLESimDialog}
				setView={setIsSimCardView}
			/>
		</React.Fragment>
	);
};
